import { useCallback, useEffect, useRef, useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { DeleteRecordButton } from '../../components/RecordActions';
import { KeyValue, Pill, Spinner, Field, SelectInput, NumberInput, TextArea, DateInput } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can, BIZ_ROLES } from '../../lib/permissions';
import { dateLabel, num } from '../../lib/format';
import { openAuthenticatedPrint } from '../../lib/print';
import { AssetTraceabilityPage } from './AssetTraceabilityPage';
import { SerialLookupPage } from './SerialLookupPage';
import { CustomerSuppliedMaterialsPage } from '../customer-supplied-materials/CustomerSuppliedMaterialsPage';
import { rolesOf } from '../../lib/permissions';
import type { RepairLedgerRow, RepairLedgerSummary, RepairLedgerResponse, WorkOrder, AssetReturnRecord, Role } from '../../lib/types';

// 送修工件（统一入口）
//
// 同一套工件事实，四签一页，避免「多模块查同一件」：
//   · 台账   = 受托工件全生命周期（主入口；关键字可搜序列号/客户/合同/工单/物料）
//   · 溯源   = 只读时间线
//   · 序列号 = 精确查询 + 同号多件消歧（/api/serials）
//   · 来料备查 = 历史客户来料加工记录（新收件不再走这里）

// 送修工件台账
//
// 为什么需要这个页面：送修的四个事实分落四张表（工件 / 维修履历 / 质检 / 工单），
// 此前互不相连，于是「工件已登记、检验也做完了，台账却查不到」。
// 本页把四段事实并成一行，并显式标出「缺哪一段关联」——
// 台账漏记比多记更难发现，所以宁可显示「待关联」也不把行藏起来。
//
// 口径：历史履历不回填（2026-09-27 决策）。待关联的行由人工在页面上选候选工单后绑定，
// 系统只做粗筛提示，不自动关联 —— 同一物料同一客户可能有多张在办单，
// 哪一张对应哪一件是业务判断。

const CYCLE_STATUS_LABEL: Record<string, { label: string; tone: 'neutral' | 'info' | 'warn' | 'ok' | 'bad' }> = {
  intake: { label: '已收件', tone: 'neutral' },
  in_production: { label: '维修中', tone: 'warn' },
  inspected: { label: '已检验', tone: 'info' },
  ready: { label: '待交货', tone: 'info' },
  delivered: { label: '已交货', tone: 'ok' },
  returned: { label: '已退回', tone: 'neutral' },
  closed: { label: '已关闭', tone: 'neutral' },
};

function cyclePill(status?: string | null) {
  if (!status) return <Pill tone="bad" label="未建履历" />;
  const s = CYCLE_STATUS_LABEL[status] ?? { label: status, tone: 'neutral' as const };
  return <Pill tone={s.tone} label={s.label} />;
}

/** 候选工单绑定面板：只在履历未认领工单时出现。 */
function LinkWorkOrderPanel({ cycleId, onLinked }: { cycleId: string; onLinked: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [picked, setPicked] = useState('');
  const { data, loading, reload } = useDetail<{ candidates: WorkOrder[] }>(
    `/api/physical-assets/cycles/${cycleId}/candidates`,
  );

  async function link() {
    if (!picked) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      await api.post(`/api/physical-assets/cycles/${cycleId}/link`, { work_order_id: picked });
      setOk('已关联工单，并把质检结论上卷到该工单的必检工序');
      reload();
      onLinked();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '关联失败');
    } finally { setBusy(false); }
  }

  if (loading && !data) return <Spinner label="加载候选工单…" />;
  const candidates = data?.candidates ?? [];
  if (!candidates.length) {
    return (
      <div className="notice">
        没有可关联的候选工单（同客户 + 同物料 + 送修类型，且尚未绑定其他履历的在办工单）。
        若工单尚未下达，请先在「生产工单」中为该合同行建单；建单时若存在未认领的履历会自动关联。
      </div>
    );
  }
  return (
    <div className="notice warn" style={{ display: 'block' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        关联生产工单（{candidates.length} 个候选）
      </div>
      <div style={{ marginBottom: 8 }}>
        候选由「同客户 + 同物料 + 送修类型 + 未绑定其他履历」粗筛得出，<b>不代表就是这一件</b>。
        请按工单号与数量核对后选择，系统不会替你判断。
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="选择工单">
          <SelectInput
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
          >
            <option value="">— 选择 —</option>
            {candidates.map((w) => (
              <option key={w.id} value={w.id}>
                {`${w.number} · ${w.status} · 合同 ${w.contract_number ?? '—'} · 数量 ${num(w.quantity, 0)}`}
              </option>
            ))}
          </SelectInput>
        </Field>
        <button className="btn" onClick={link} disabled={busy || !picked}>
          {busy ? '关联中…' : '确认关联'}
        </button>
      </div>
      {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
      {ok ? <div className="notice" style={{ marginTop: 8 }}>{ok}</div> : null}
    </div>
  );
}

/**
 * 退料 → 退货单（送修工件无法修复，退回客户）
 *
 * 边界（LOCKED）：客户送修工件是「受托保管」，不是公司库存 —— 不能报废，
 * 修不好只能退货给客户。这里走的正是 /api/asset-return 的 draft→confirmed→signed
 * 权威链：签收（sign）才写 'returned' 流水并扣减在厂数，与交货出库互斥。
 * 退货单版式沿用交货单（公司→客户），只是名称不同，由 core-api 打印 asset-return。
 *
 * 幂等：source_id 在面板挂载时生成一次，重复点击创建不会落多张草稿。
 */
function ReturnPanel({ row, onChanged }: { row: RepairLedgerRow; onChanged: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const srcRef = useRef<string>('');
  if (!srcRef.current) {
    const rnd = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    srcRef.current = `ledger/${row.asset_id}/${row.cycle_id}/${rnd}`;
  }

  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState('');
  const [retDate, setRetDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [rec, setRec] = useState<AssetReturnRecord | null>(null);
  const [printing, setPrinting] = useState(false);

  async function create() {
    if (!reason.trim()) { setErr('退料原因必填：修不好退回客户，需记录原因'); return; }
    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) { setErr('退料数量必须是大于 0 的数字'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post<{ return: AssetReturnRecord; message?: string }>('/api/asset-return/create', {
        service_cycle_id: row.cycle_id,
        quantity: q,
        reason: reason.trim(),
        return_date: retDate || today,
        notes: notes.trim() || undefined,
        contract_id: row.contract_id || undefined,
        source_system: 'web-console',
        source_id: srcRef.current,
      });
      setRec(r.return);
      setMsg(r.message || `退货单已创建：${r.return.number}（草稿，签收时扣减在厂数）`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建退货单失败');
    } finally { setBusy(false); }
  }

  async function advance(next: 'confirm' | 'sign') {
    if (!rec) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post<{ return: AssetReturnRecord; message?: string }>(`/api/asset-return/${next}`, { return_id: rec.id });
      setRec(r.return);
      setMsg(r.message || (next === 'sign' ? '已签收归还，在厂数已扣减' : '已确认，待客户签收'));
      if (next === 'sign') onChanged(); // 在厂数变化，刷新台账
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(false); }
  }

  async function doPrint() {
    if (!rec) return;
    setPrinting(true); setErr(null);
    const r = await openAuthenticatedPrint('asset-return', rec.id);
    if (!r.ok) setErr(r.message || '打印失败');
    setPrinting(false);
  }

  const signed = rec?.status === 'signed';
  const confirmed = rec?.status === 'confirmed';
  const isDraft = rec?.status === 'draft';

  return (
    <div className="notice" style={{ display: 'block', background: 'var(--panel-inset)', marginTop: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>退料 / 退货单（工件无法修复，退回客户）</div>
      <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ink-3)' }}>
        送修工件由我方受托保管，<b>不能报废、不计入公司库存</b>；确认无法修复时开一张退货单归还客户。
        签收（最后一步）才会写归还流水并扣减在厂数。
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="退料数量">
          <NumberInput value={qty} min={1} step={1} onChange={(e) => setQty(e.target.value)} disabled={!!rec} />
        </Field>
        <Field label="退料日期">
          <DateInput value={retDate} onChange={(e) => setRetDate(e.target.value)} disabled={!!rec} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 6 }}>
        <Field label="退料原因" span required>
          <TextArea value={reason} placeholder="如：激光管击穿，超出维修范围，退还原客户"
            onChange={(e) => setReason(e.target.value)} disabled={!!rec} />
        </Field>
        <Field label="备注" span>
          <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!!rec} />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        {!rec ? (
          <button className="btn" onClick={create} disabled={busy || !reason.trim()}>
            {busy ? '创建中…' : '创建退货单'}
          </button>
        ) : null}
        {rec && isDraft ? (
          <button className="btn" onClick={() => advance('confirm')} disabled={busy}>
            {busy ? '处理中…' : '确认退货单'}
          </button>
        ) : null}
        {rec && (isDraft || confirmed) ? (
          <button className="btn" onClick={() => advance('sign')} disabled={busy}>
            {busy ? '处理中…' : '签收归还（扣减在厂数）'}
          </button>
        ) : null}
        {rec ? (
          <button className="btn ghost" onClick={doPrint} disabled={printing}>
            {printing ? '打印中…' : '打印退货单'}
          </button>
        ) : null}
        {rec ? (
          <span className="num muted" style={{ fontSize: 12 }}>
            {rec.number} · {{ draft: '草稿', confirmed: '已确认', signed: '已签收', cancelled: '已取消' }[rec.status] ?? rec.status}
          </span>
        ) : null}
        {signed ? <Pill tone="ok" label="已归还·在厂数已扣减" /> : null}
      </div>

      {err ? <div className="notice bad" style={{ marginTop: 8 }}>{err}</div> : null}
      {msg ? <div className="notice" style={{ marginTop: 8 }}>{msg}</div> : null}
    </div>
  );
}

function RowDetail({ row, canLink, canReturn, onChanged }: { row: RepairLedgerRow; canLink: boolean; canReturn: boolean; onChanged: () => void }) {
  const insp = Number(row.inspection_count || 0);
  const acc = Number(row.inspection_accepted || 0);
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.customer_serial}</span>
        <h3>{`${row.item_code ?? ''} ${row.item_name_zh ?? ''}`.trim() || '—'}</h3>
        {cyclePill(row.cycle_status)}
        {row.pending_manual_link ? <Pill tone="bad" label="待关联" /> : null}
        <div className="spacer" />
      </div>

      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KeyValue k="客户">{`${row.customer_code ?? ''} ${row.customer_name ?? ''}`.trim() || '—'}</KeyValue>
        <KeyValue k="内部编码">{row.internal_code || '—'}</KeyValue>
        <KeyValue k="工件状态">{row.asset_status || '—'}</KeyValue>
        <KeyValue k="收件日期">{dateLabel(row.first_received_date ?? row.intake_date)}</KeyValue>
        <KeyValue k="维修履历">{row.cycle_id ? `V${row.cycle_version} · ${row.cycle_status}` : '未建履历'}</KeyValue>
        <KeyValue k="返工次数">{num(row.rework_count, 0)}</KeyValue>
        <KeyValue k="所属合同">{row.contract_number || <span className="muted">未关联</span>}</KeyValue>
        <KeyValue k="关联工单">{row.work_order_number || <span className="muted">未关联</span>}</KeyValue>
        <KeyValue k="工单状态">{row.work_order_status || '—'}</KeyValue>
        <KeyValue k="已产出">{num(row.produced_quantity, 0)}</KeyValue>
        <KeyValue k="质检单">
          {insp ? (
            <>
              {`${insp} 张（合格 ${acc}`}
              {Number(row.inspection_rejected || 0) > 0 ? `，不合格 ${row.inspection_rejected}` : ''}
              {Number(row.inspection_pending || 0) > 0 ? `，待出结论 ${row.inspection_pending}` : ''}
              {'）'}
            </>
          ) : <span className="muted">无</span>}
        </KeyValue>
        <KeyValue k="最近检验">{dateLabel(row.last_inspection_date)}</KeyValue>
        <KeyValue k="交货单">
          {row.delivery_number ? `${row.delivery_number} · ${row.delivery_status}` : <span className="muted">未交货</span>}
        </KeyValue>
      </div>

      {row.cycle_id && row.cycle_status === 'intake' ? (
        <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>检验发现 / 维修建议</div>
      ) : null}
      {row.findings || row.recommendation ? (
        <div className="kv-grid" style={{ marginBottom: 8 }}>
          <KeyValue k="检验发现">{row.findings || '—'}</KeyValue>
          <KeyValue k="维修建议">{row.recommendation || '—'}</KeyValue>
        </div>
      ) : null}

      {row.cycle_id && row.pending_manual_link ? (
        canLink
          ? <LinkWorkOrderPanel cycleId={row.cycle_id} onLinked={onChanged} />
          : (
            <div className="notice warn">
              该维修履历尚未关联生产工单，合同与工单信息在台账上无法呈现。
              关联操作需要「实物资产」编辑权限，该权限当前仅授予管理员
              （权限表 physical-assets.edit = ['admin']）。
            </div>
          )
      ) : null}

      {row.cycle_id && !row.pending_manual_link && !insp ? (
        <div className="notice">
          该工件的维修履历还没有任何质检单。若检验已在现场完成，需要把结论同步到统一核心，
          否则台账与工序状态都会停在「未检」。
        </div>
      ) : null}

      {row.cycle_id && row.cycle_status !== 'closed' ? (
        canReturn
          ? <ReturnPanel row={row} onChanged={onChanged} />
          : (
            <div className="notice" style={{ background: 'var(--panel-inset)', marginTop: 12 }}>
              退料 / 退货单：工件确认无法修复时归还客户（签收后扣减在厂数）。
              该操作需要退料权限，当前角色不可用（后端 /api/asset-return 写权限 = 生产·质检·仓储·销售）。
            </div>
          )
      ) : null}

      {row.cycle_id ? (
        <div className="row-actions" style={{ marginTop: 10 }}>
          {/* 管理员 + 业务操作员（BIZ_ROLES）可见，与后端路由放开范围一致：
              测试记录清理 = 删领退料流水 + 删维修版本，工程师独立完成整条链。 */}
          <DeleteRecordButton
            type="asset-service-cycles"
            id={row.cycle_id}
            label={`维修版本 V${row.cycle_version}`}
            minRoles={BIZ_ROLES}
            note={row.work_order_number ? `同时断开与工单 ${row.work_order_number} 的双向绑定，该工单随后可作废或删除。` : undefined}
            onDone={onChanged}
          />
        </div>
      ) : null}
    </div>
  );
}

type WorkpieceTab = 'ledger' | 'trace' | 'serial' | 'incoming';

export function RepairLedgerPage() {
  const [tab, setTab] = useState<WorkpieceTab>('ledger');
  return (
    <div>
      <div className="panel" style={{ marginBottom: 'var(--gap)', boxShadow: 'none' }}>
        <div className="panel-head" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <h2 style={{ marginRight: 8 }}>送修工件</h2>
          {([
            ['ledger', '台账'],
            ['trace', '溯源'],
            ['serial', '序列号'],
            ['incoming', '来料备查'],
          ] as [WorkpieceTab, string][]).map(([key, label]) => (
            <button
              key={key}
              className={`btn${tab === key ? ' primary' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
          <div className="spacer" />
          <div className="desc" style={{ margin: 0 }}>
            同一套工件事实：台账（可搜序列号/客户/合同）· 溯源 · 序列号精确查询 · 来料备查
          </div>
        </div>
      </div>
      {tab === 'ledger' ? <RepairLedgerInner /> : null}
      {tab === 'trace' ? <AssetTraceabilityPage /> : null}
      {tab === 'serial' ? <SerialLookupPage /> : null}
      {tab === 'incoming' ? <CustomerSuppliedMaterialsPage /> : null}
    </div>
  );
}

function RepairLedgerInner() {
  const { user } = useAuth();
  const canLink = can(user, 'physical-assets', 'edit');
  // 退料 / 退货单写权限：与后端 /api/asset-return(create|confirm|sign) 角色对齐。
  const canReturn = (() => {
    // 统一身份角色经 rolesOf 展开后再判定（与后端 integral-role-map 同源）。
  const roles = rolesOf(user);
  return roles.includes('admin') || (['production', 'quality', 'warehouse', 'sales'] as Role[]).some((x) => roles.includes(x));
  })();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [summary, setSummary] = useState<RepairLedgerSummary | null>(null);
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [cycleStatus, setCycleStatus] = useState('');

  // 汇总与列表用同一个筛选口径，否则计数和列表会对不上（047/048 视图曾经踩过这个坑）
  const query = `limit=200${onlyUnlinked ? '&unlinked=1' : ''}${cycleStatus ? `&cycle_status=${cycleStatus}` : ''}`;
  const endpoint = `/api/physical-assets/repair-ledger?${query}`;

  // 只要计数，不重复拉 200 行（与 ListPage 的请求共用 where 条件）
  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get<RepairLedgerResponse>(`/api/physical-assets/repair-ledger?summary_only=1&${query}`);
      setSummary(r.summary);
    } catch { /* 汇总失败不阻断列表 */ }
  }, [query]);

  useEffect(() => { void loadSummary(); }, [loadSummary, reloadSignal]);

  const columns: Column<RepairLedgerRow>[] = [
    { header: '工件序列号', render: (r) => <span className="num strong">{r.customer_serial}</span> },
    { header: '客户', render: (r) => <span className="muted">{r.customer_name || '—'}</span> },
    { header: '物料', render: (r) => `${r.item_code ?? ''} ${r.item_name_zh ?? ''}`.trim() || '—' },
    { header: '维修履历', render: (r) => (r.cycle_id ? <Pill tone="info" label={`V${r.cycle_version}`} /> : <Pill tone="bad" label="未建履历" />) },
    { header: '履历状态', render: (r) => cyclePill(r.cycle_status) },
    { header: '所属合同', render: (r) => (r.contract_number ? <span className="num">{r.contract_number}</span> : <span className="muted">未关联</span>) },
    { header: '工单', render: (r) => (r.work_order_number ? <span className="num">{r.work_order_number}</span> : <span className="muted">未关联</span>) },
    {
      header: '质检',
      align: 'right',
      // 张数 / 合格 / 不合格必须分开显示：「检了 8 次、合格 8 次」在返修件上
      // 和一次过件长得一样，读表人会以为已完成。判过不合格的要显式标出来。
      render: (r) => {
        const n = Number(r.inspection_count || 0);
        if (!n) return <span className="muted">无</span>;
        const acc = Number(r.inspection_accepted || 0);
        const rej = Number(r.inspection_rejected || 0);
        const pend = Number(r.inspection_pending || 0);
        if (rej > 0) return <Pill tone="bad" label={`${acc}合格 / ${rej}不合格 (共${n})`} />;
        if (pend > 0) return <Pill tone="warn" label={`${acc}/${n}，${pend}待检`} />;
        return acc === n
          ? <Pill tone="ok" label={`${n} 合格`} />
          : <Pill tone="warn" label={`${acc}/${n}`} />;
      },
    },
    { header: '交货', render: (r) => (r.delivery_number ? <Pill tone="ok" label={r.delivery_status ?? '已交货'} /> : <span className="muted">未交货</span>) },
    {
      header: '关联',
      render: (r) => (r.pending_manual_link
        ? <Pill tone="bad" label={Number(r.candidate_work_orders) > 0 ? `待关联·${r.candidate_work_orders}候选` : '待关联'} />
        : <Pill tone="ok" label="已关联" />),
    },
  ];

  return (
    <>
      {summary ? (
        <div className="kv-grid" style={{ marginBottom: 12 }}>
          <KeyValue k="送修工件">{num(summary.assets, 0)}</KeyValue>
          <KeyValue k="已检验">{num(summary.inspected, 0)}</KeyValue>
          <KeyValue k="已关联工单">{num(summary.assets - summary.without_work_order, 0)}</KeyValue>
          <KeyValue k="待人工关联">{num(summary.pending_manual_link, 0)}</KeyValue>
          <KeyValue k="有候选工单">{num(summary.has_candidates, 0)}</KeyValue>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
        <Field label="履历状态">
          <SelectInput value={cycleStatus} onChange={(e) => setCycleStatus(e.target.value)}>
            <option value="">全部</option>
            {Object.entries(CYCLE_STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </SelectInput>
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} />
          只看待人工关联
        </label>
      </div>

      <ListPage<RepairLedgerRow>
        title="送修工件台账"
        desc="客户受托工件唯一主台账 · 收件→维修版本→质检→工单→交货 / 无法修复退回。退货单（RET-）独立于销售合同，对应客户原送货单；不进公司库存。"
        endpoint={endpoint}
        columns={columns}
        rowId={(r) => r.asset_id}
        showYear={false}
        searchPlaceholder="搜索工件序列号 / 内部编码 / 客户 / 合同号 / 工单号 / 物料"
        reloadSignal={reloadSignal}
        actions={
          <button className="btn ghost" onClick={() => { loadSummary(); setReloadSignal((n) => n + 1); }}>
            刷新
          </button>
        }
        renderDetail={(row) => (
          <RowDetail row={row} canLink={canLink} canReturn={canReturn} onChanged={() => setReloadSignal((n) => n + 1)} />
        )}
      />
    </>
  );
}
